import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import type { ApiError } from "../api/client";
import { STATUS_LABEL } from "../lib/format";
import { t } from "../i18n";
import { IconEye, IconEyeOff } from "./icons";

/* ------------------------------------------------------------------ botões */

type Variant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "secondary",
  size,
  busy,
  block,
  className = "",
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm"; busy?: boolean; block?: boolean }) {
  const cls = ["btn", `btn-${variant}`, size === "sm" && "btn-sm", block && "btn-block", className]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy && <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} aria-hidden />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ campos */

interface FieldBase {
  label: string;
  error?: string;
  hint?: string;
}

function FieldWrap({ id, label, error, hint, children }: FieldBase & { id: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && !error && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: string) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}

export function TextField({ label, error, hint, ...rest }: FieldBase & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const isPassword = rest.type === "password";
  const [reveal, setReveal] = useState(false);
  const input = (
    <input
      id={id}
      className="input"
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(id, error, hint)}
      {...rest}
      type={isPassword && reveal ? "text" : rest.type}
    />
  );
  return (
    <FieldWrap id={id} label={label} error={error} hint={hint}>
      {isPassword ? (
        // Senha: botão para mostrar/ocultar (evita erro de digitação no celular; o gerenciador de senhas segue funcionando)
        <div className="password-wrap">
          {input}
          <button
            type="button"
            className="password-toggle"
            aria-controls={id}
            aria-pressed={reveal}
            aria-label={t("Mostrar senha")}
            title={t("Mostrar senha")}
            onClick={() => setReveal((v) => !v)}
          >
            {reveal ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
      ) : (
        input
      )}
    </FieldWrap>
  );
}

export function SelectField({
  label,
  error,
  hint,
  children,
  ...rest
}: FieldBase & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <FieldWrap id={id} label={label} error={error} hint={hint}>
      <select
        id={id}
        className="select"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      >
        {children}
      </select>
    </FieldWrap>
  );
}

export function TextArea({ label, error, hint, ...rest }: FieldBase & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <FieldWrap id={id} label={label} error={error} hint={hint}>
      <textarea
        id={id}
        className="textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      />
    </FieldWrap>
  );
}

export function FormError({ error }: { error: ApiError | null }) {
  if (!error) return null;
  return (
    <div className="alert alert-error" role="alert">
      <strong>{error.message}</strong>
      {error.details.length > 0 && error.code !== "validation_error" && (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {error.details.map((d, i) => (
            <li key={i}>{d.message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ cabeçalho de página */

export function PageHead({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="title-block">
        <h1>{title}</h1>
        <div className="lane" aria-hidden />
        {subtitle && <p className="subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ status */

export function StatusBadge({ status }: { status: string }) {
  const cls = status === "active" ? "badge-ok" : status === "paused" ? "badge-warn" : "badge-off";
  return <span className={`badge ${cls}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function Badge({ tone = "off", children }: { tone?: "ok" | "warn" | "off" | "danger"; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

/* ------------------------------------------------------------------ estados */

export function Loading({ label = t("Carregando…") }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  return (
    <div className="state" role="alert">
      <h3>{t("Não foi possível carregar")}</h3>
      <p>{error.message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="secondary">
          {t("Tentar novamente")}
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="state">
      <h3>{title}</h3>
      {children && <p style={{ maxWidth: "60ch" }}>{children}</p>}
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ paginação */

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav className="pagination" aria-label={t("Paginação")}>
      <span>
        {t("{a}–{b} de {total}", { a: from, b: to, total })}
      </span>
      <span style={{ display: "flex", gap: 6 }}>
        <Button size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
          {t("Anterior")}
        </Button>
        <Button size="sm" onClick={() => onPage(page + 1)} disabled={page >= pages}>
          {t("Próxima")}
        </Button>
      </span>
    </nav>
  );
}

/* ------------------------------------------------------------------ modal (dialog nativo, acessível) */

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`modal${wide ? " wide" : ""}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {open && (
        <>
          <div className="modal-head">
            <h2 id={titleId}>{title}</h2>
            <div className="lane" aria-hidden />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

/* ------------------------------------------------------------------ avisos (toasts) */

interface Toast {
  id: number;
  text: string;
  kind: "ok" | "error";
}
const ToastContext = createContext<(text: string, kind?: "ok" | "error") => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: "ok" | "error" = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((tp) => [...tp, { id, text, kind }].slice(-3));
    setTimeout(() => setToasts((tp) => tp.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((tp) => (
          <div key={tp.id} className={`toast ${tp.kind === "error" ? "error" : ""}`}>
            {tp.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
